import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { FiArrowLeft, FiMapPin, FiCheck, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Card from '../Components/Card';
import FormField from '../Components/FormField';
import axiosClient from '../Api/axiosClient';
import { useRetailer } from '../hooks/useRetailers';
import { useRecordCheckIn } from '../hooks/useCheckins';
import { useSaveDraft } from '../hooks/useDrafts';
import { getDraft, deleteDraft } from '../Api/draftsApi';

export default function CheckInPage() {
  const { cmGuid } = useParams();
  const navigate = useNavigate();
  const { data: retailer } = useRetailer(cmGuid);
  const record = useRecordCheckIn(cmGuid);
  const saveDraftM = useSaveDraft();
  const [params] = useSearchParams();
  const draftFromUrl = params.get('draft');
  const [draftGuid] = useState(draftFromUrl || null);

  const [coords, setCoords] = useState({ lat: '', lng: '', accuracy: null });
  const [address, setAddress] = useState('');
  const [locating, setLocating] = useState(false);
  const [form, setForm] = useState({ visitOutcome: '', notes: '', nextSteps: '', nextVisitPlannedDate: '' });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Resume a saved check-in draft
  useEffect(() => {
    if (!draftFromUrl) return;
    getDraft(draftFromUrl).then((d) => {
      if (!d?.payloadJson) return;
      try {
        const p = JSON.parse(d.payloadJson);
        if (p.coords) setCoords(p.coords);
        if (p.address) setAddress(p.address);
        setForm((f) => ({ ...f, visitOutcome: p.visitOutcome || '', notes: p.notes || '', nextSteps: p.nextSteps || '', nextVisitPlannedDate: p.nextVisitPlannedDate || '' }));
      } catch { /* ignore */ }
    });
  }, [draftFromUrl]);

  const onSaveDraft = () => {
    const payload = { retailerCmGuid: cmGuid, coords, address, ...form };
    saveDraftM.mutate(
      { poCode: 'SFVMCKPI', title: `Check-in: ${retailer?.customerName || cmGuid}`, payloadJson: JSON.stringify(payload), draftGuid: draftGuid || null },
      { onSuccess: () => navigate('/checkins/drafts') }
    );
  };

  const capture = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not available.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        setCoords({ lat: String(lat), lng: String(lng), accuracy: pos.coords.accuracy });
        setLocating(false);
        setAddress('Resolving address…');
        axiosClient.get('/lookups/reverse-geocode', { params: { lat, lng } })
          .then((r) => setAddress(r.data?.data?.description || '(no address found)'))
          .catch(() => setAddress(''));
      },
      (err) => { toast.error(err.message || 'Could not get location.'); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!coords.lat || !coords.lng) { toast.error('Capture location before checking in.'); return; }
    record.mutate(
      {
        retailerCmGuid: cmGuid,
        latitude: coords.lat,
        longitude: coords.lng,
        address: (address && !address.startsWith('Resolving') && address !== '(no address found)') ? address : null,
        accuracy: coords.accuracy,
        visitOutcome: form.visitOutcome || null,
        notes: form.notes || null,
        nextSteps: form.nextSteps || null,
        nextVisitPlannedDate: form.nextVisitPlannedDate || null,
      },
      { onSuccess: () => { if (draftGuid) deleteDraft(draftGuid).catch(() => {}); navigate(`/retailers/${cmGuid}`); } }
    );
  };

  return (
    <div className="hrms-page">
      <div className="hrms-toolbar">
        <h1 className="hrms-page-title">Check-in{retailer?.customerName ? ` — ${retailer.customerName}` : ''}</h1>
        <Button variant="light" onClick={() => navigate(`/retailers/${cmGuid}`)}><FiArrowLeft className="me-1" /> Back</Button>
      </div>

      <Form onSubmit={submit}>
        <Card title="Location" className="mb-3">
          <div className="d-flex align-items-center gap-3">
            <Button variant="light" onClick={capture} disabled={locating}>
              <FiMapPin className="me-1" /> {locating ? 'Locating…' : 'Capture location'}
            </Button>
            <span className="hrms-muted">
              {coords.lat ? `${coords.lat}, ${coords.lng}${coords.accuracy ? ` (±${Math.round(coords.accuracy)}m)` : ''}` : 'Not captured'}
            </span>
          </div>
          {address && <div className="hrms-muted" style={{ marginTop: 8 }}><FiMapPin className="me-1" />{address}</div>}
        </Card>

        <Card title="Visit details (optional)" className="mb-3">
          <Row>
            <Col md={6}><FormField label="Visit outcome"><Form.Control value={form.visitOutcome} onChange={(e) => set({ visitOutcome: e.target.value })} maxLength={100} /></FormField></Col>
            <Col md={6}><FormField label="Next visit planned"><Form.Control type="date" value={form.nextVisitPlannedDate} onChange={(e) => set({ nextVisitPlannedDate: e.target.value })} /></FormField></Col>
            <Col md={12}><FormField label="Notes"><Form.Control as="textarea" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></FormField></Col>
            <Col md={12}><FormField label="Next steps"><Form.Control as="textarea" rows={2} value={form.nextSteps} onChange={(e) => set({ nextSteps: e.target.value })} /></FormField></Col>
          </Row>
          <div className="hrms-muted" style={{ fontSize: '0.78rem' }}>Photo capture will be added once attachment upload is wired.</div>
        </Card>

        <div className="d-flex gap-2">
          <Button type="submit" className="hrms-btn-primary" disabled={record.isPending || saveDraftM.isPending}>
            <FiCheck className="me-1" /> {record.isPending ? 'Saving…' : 'Record check-in'}
          </Button>
          <Button variant="outline-secondary" onClick={onSaveDraft} disabled={record.isPending || saveDraftM.isPending}>
            <FiFileText className="me-1" /> Save as draft
          </Button>
          <Button variant="light" onClick={() => navigate(`/retailers/${cmGuid}`)} disabled={record.isPending || saveDraftM.isPending}>Cancel</Button>
        </div>
      </Form>
    </div>
  );
}
